import type { MaterialData } from "../api/types/Product";
import type { MaterialGroup } from "../api/types/Catalog";
import { API_BASE_URL } from "../config/api";

const MATERIAL_SERVICE = `${API_BASE_URL}/material`;
const MATERIAL_GROUP_SERVICE = `${API_BASE_URL}/material-group`;
const LOG = "[MaterialInfoCache]";

const materialById = new Map<string, MaterialData>();
const materialByCode = new Map<string, MaterialData>();

let inflight: Promise<void> | null = null;
/**
 * false iken eksik materyaller sunucudan çekilmez. Ürün editörünün mock modu kullanır:
 * sahte materyaller indexMaterialRows ile önceden kaydedilir, gerçek API'ye istek gitmez.
 */
let remoteFetchEnabled = true;

export function setMaterialRemoteFetch(enabled: boolean): void {
  remoteFetchEnabled = enabled;
}
const pendingKeys = new Set<string>();
let pendingCompanyId: number | undefined;

export const isValidMaterialKey = (
  v: unknown,
): v is string | number => v != null && v !== "" && v !== "0" && v !== 0;

function lookup(key: string | number): MaterialData | undefined {
  const k = String(key).trim();
  return materialById.get(k) ?? materialByCode.get(k.toUpperCase()) ?? materialByCode.get(k);
}

function putCode(code: string | number, row: MaterialData): void {
  const normalized = String(code).trim();
  materialByCode.set(normalized, row);
  materialByCode.set(normalized.toUpperCase(), row);
}

function indexRow(row: MaterialData): void {
  if (row?.id == null) return;
  materialById.set(String(row.id), row);
  const code = (row as MaterialData & { code?: string }).code;
  if (isValidMaterialKey(code)) putCode(code, row);
  if (isValidMaterialKey(row.groupBy_)) putCode(row.groupBy_!, row);
  if (row.name?.trim()) putCode(row.name.trim(), row);
}

/** Hazır materyal satırlarını cache'e yazar (API fetch olmadan). */
export function indexMaterialRows(
  rows: Array<MaterialData | Record<string, unknown>>,
): void {
  for (const row of rows) {
    if (row && typeof row === "object" && row.id != null) {
      indexRow(row as MaterialData);
    }
  }
}

export type MaterialPartGroupsSeed = Array<{
  materialGroups?: Array<{ materials?: Array<MaterialData | Record<string, unknown>> }>;
}>;

/** Configurator zip'teki materialPartGroups → MaterialInfoCache. */
export function seedMaterialPartGroupsCache(
  materialPartGroups?: MaterialPartGroupsSeed,
): void {
  if (!materialPartGroups?.length) return;

  for (const partGroup of materialPartGroups) {
    for (const materialGroup of partGroup.materialGroups ?? []) {
      indexMaterialRows(materialGroup.materials ?? []);
    }
  }
}

function parseRows(payload: unknown): MaterialData[] {
  if (Array.isArray(payload)) return payload as MaterialData[];
  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    if (Array.isArray(p.data)) return p.data as MaterialData[];
    if (Array.isArray(p.materials)) return p.materials as MaterialData[];
    if (p.id != null) return [p as unknown as MaterialData];
  }
  return [];
}

async function flushFetch(): Promise<void> {
  const keys = [...pendingKeys].filter((key) => !lookup(key));
  pendingKeys.clear();
  const companyId = pendingCompanyId;
  pendingCompanyId = undefined;

  if (!keys.length || !remoteFetchEnabled) return;

  const params = new URLSearchParams();
  params.set("ids", keys.join(","));
  if (companyId != null) params.set("companyId", String(companyId));

  const url = `${MATERIAL_SERVICE}/by-id?${params.toString()}`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(LOG, "fetch failed", res.status, url);
      return;
    }
    const json = await res.json();
    if (json?.error && json?.status != 200) return;

    for (const row of parseRows(json)) indexRow(row);
    console.info(LOG, "cached", keys.join(","));
  } catch (error) {
    console.error(LOG, "fetch error:", error);
  }
}

function scheduleFetch(companyId?: number): Promise<void> {
  if (companyId != null) pendingCompanyId = companyId;

  if (inflight) return inflight;

  inflight = new Promise<void>((resolve) => {
    queueMicrotask(async () => {
      try {
        await flushFetch();
      } finally {
        inflight = null;
        resolve();
      }
    });
  });

  return inflight;
}

/** Aynı tick'teki istekleri birleştirir; `/api/material/by-id` batch fetch. */
export async function ensureMaterialInfosCached(
  keys: Array<string | number | null | undefined>,
  companyId?: number,
): Promise<void> {
  const missing = [...new Set(keys.filter(isValidMaterialKey).map(String))].filter(
    (key) => !lookup(key),
  );
  if (!missing.length) return;

  missing.forEach((key) => pendingKeys.add(key));
  await scheduleFetch(companyId);

  const stillMissing = missing.filter((key) => !lookup(key));
  if (stillMissing.length && pendingKeys.size) {
    await ensureMaterialInfosCached(stillMissing, companyId);
  }
}

/** Şirketin material group'ları (+ içindeki materyaller). Tag service `GET /api/material-group/by-company`. */
export async function fetchMaterialGroupsByCompany(
  companyId: number,
  searchKey?: string,
): Promise<MaterialGroup[]> {
  const params = new URLSearchParams({ companyId: String(companyId) });
  if (searchKey?.trim()) params.set("searchKey", searchKey.trim());

  const url = `${MATERIAL_GROUP_SERVICE}/by-company?${params.toString()}`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(LOG, "groups fetch failed", res.status, url);
      return [];
    }
    const json = await res.json();
    const groups = Array.isArray(json) ? (json as MaterialGroup[]) : [];
    seedMaterialPartGroupsCache([{ materialGroups: groups }]);
    return groups;
  } catch (error) {
    console.error(LOG, "groups fetch error:", error);
    return [];
  }
}

export function getCachedMaterialRow(
  key: string | number,
): MaterialData | undefined {
  return lookup(key);
}

const isNumericMaterialId = (s: string) => /^\d+$/.test(s);

/** Rapid zone satırı: `materialId` CRM id, `code` Rapid kumaş kodu. */
export type ZoneMaterialRef = {
  code?: string | null;
  materialId?: number | null;
};

/**
 * Katalogda / ürün materials[] içinde olmayan kumaşları
 * `GET /api/material/by-id?ids=` ile çeker; Rapid `code`'u aynı satıra alias'lar.
 * SMV2 `prefetchMaterialsForZoneAreas` ile aynı yol.
 */
export async function prefetchZoneMaterialRefs(
  refs: ZoneMaterialRef[],
  companyId?: number,
): Promise<void> {
  const ids = [
    ...new Set(
      refs
        .map((ref) => ref.materialId)
        .filter((id): id is number => id != null && id !== 0)
        .map(String)
        .filter(isNumericMaterialId),
    ),
  ];
  if (ids.length) await ensureMaterialInfosCached(ids, companyId);

  for (const ref of refs) {
    if (ref.materialId == null || ref.materialId === 0) continue;
    if (!isValidMaterialKey(ref.code)) continue;
    const row = materialById.get(String(ref.materialId));
    if (row) putCode(ref.code, row);
  }
}

export async function prefetchMaterialsForZoneAreas(
  areas: Array<{
    selected?: ZoneMaterialRef | null;
    options?: ZoneMaterialRef[];
  }>,
  companyId?: number,
): Promise<void> {
  const refs: ZoneMaterialRef[] = [];
  for (const area of areas) {
    if (area.selected) refs.push(area.selected);
    if (area.options) refs.push(...area.options);
  }
  await prefetchZoneMaterialRefs(refs, companyId);
}

export function collectMaterialKeysFromProduct(data: {
  partMaterialGroups?: Array<{
    code?: string;
    defaultMaterialCode?: string | null;
    materials?: number[] | null;
  }>;
  parts?: Array<{
    code?: string;
    defaultMaterialCode?: string | null;
    materials?: number[] | null;
  }>;
}): Array<string | number> {
  const keys: Array<string | number> = [];

  for (const group of data.partMaterialGroups ?? []) {
    if (isValidMaterialKey(group.defaultMaterialCode)) {
      keys.push(group.defaultMaterialCode!);
    }
    if (isValidMaterialKey(group.code)) keys.push(group.code);
    if (group.materials) keys.push(...group.materials.filter(isValidMaterialKey));
  }

  for (const part of data.parts ?? []) {
    if (isValidMaterialKey(part.defaultMaterialCode)) {
      keys.push(part.defaultMaterialCode!);
    }
    if (isValidMaterialKey(part.code)) keys.push(part.code);
    if (part.materials) keys.push(...part.materials.filter(isValidMaterialKey));
  }

  return keys;
}

export function clearMaterialInfoCache(): void {
  materialById.clear();
  materialByCode.clear();
  pendingKeys.clear();
  pendingCompanyId = undefined;
  inflight = null;
}
